import React, { useMemo } from 'react';
import { observer } from 'mobx-react-lite';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import ITreeNode from '../tree/ITreeNode';
import InternalTreeNode from './components/InternalTreeNode';
import { flattenTree, removeChildrenOf, FlattenedTreeNode } from './utilities';

interface Props {
  nodes: ITreeNode[];
  allNodes: ITreeNode[];
  root: string | number;
  activeId?: string | number | null;
  projected?: {
    depth: number;
    parentId: string | number | null;
  } | null;
  indentationWidth?: number;
}

const InternalSitemapTree: React.FC<Props> = observer(({ 
  nodes, 
  allNodes, 
  root, 
  activeId,
  projected,
  indentationWidth = 20
}) => {
  // Filter to get only root-level nodes
  const rootNodes = nodes.filter(node => node.parent === root);
  
  // Default pages should always be at the bottom and locked
  const isDefaultRoot = (node: ITreeNode) => 
    node.data.type === 'default' || node.data.type === 'Startseite';
  
  const sortedRootNodes = [
    ...rootNodes
      .filter(node => !isDefaultRoot(node))
      .sort((a, b) => {
        if (a.data.order !== undefined && b.data.order !== undefined) {
          return a.data.order - b.data.order;
        }
        if (a.data.order !== undefined) return -1;
        if (b.data.order !== undefined) return 1;
        return 0;
      }),
    ...rootNodes.filter(isDefaultRoot)
  ];

  // Flatten the tree structure and add external page as ghost if being dragged
  const flattenedItems = useMemo(() => {
    let flattened = flattenTree(sortedRootNodes, allNodes);
    
    // Remove children of the active item when dragging (if it's internal)
    if (activeId != null) {
      const activeNode = allNodes.find(n => n.id === activeId);
      const isExternal = activeNode?.data.origin === 'external';
      
      if (!isExternal) {
        flattened = removeChildrenOf(flattened, [activeId]);
      } else if (activeNode && projected) {
        // Add external page as ghost placeholder at projected position
        const ghostItem = {
          ...activeNode,
          depth: projected.depth,
          index: flattened.length
        };
        flattened.push(ghostItem);
      }
    }
    
    return flattened;
  }, [sortedRootNodes, allNodes, activeId, projected]);

  const sortedIds = useMemo(
    () => flattenedItems.map(({ id }) => id),
    [flattenedItems]
  );

  return (
    <SortableContext items={sortedIds} strategy={verticalListSortingStrategy}>
      {flattenedItems.map((item) => {
        const isIndexPage = item.data.type === 'Startseite';
        const isDefaultPage = item.data.type === 'default';
        // Check if this is the ghost of the dragged item
        const isGhost = item.id === activeId;
        const showAsProjected = isGhost && projected;
        // Show folder icon only if this is a drop target during drag
        const isDropTarget = !!activeId && !isDefaultPage && !isIndexPage && item.depth === 0 && activeId !== item.id && projected && projected.parentId === item.id;
        const showFolderIcon = isDropTarget;
        return (
          <InternalTreeNode
            key={item.id}
            node={item}
            depth={showAsProjected ? projected.depth : item.depth}
            indentationWidth={indentationWidth}
            ghost={isGhost}
            indicator={false}
            isIndexPage={isIndexPage}
            isDefaultPage={isDefaultPage}
            showFolderIcon={showFolderIcon}
            allNodes={allNodes}
            projected={projected}
            activeId={activeId}
          />
        );
      })}
    </SortableContext>
  );
});

export default InternalSitemapTree;
